import ClassNames from "classnames";
import * as React from "react";
import { createPortal } from "react-dom";

import IconClose from "../_assets/icon-close-circle.svg";
import Styles from "./Modal.module.css";

import ButtonIcon from "../ButtonIcon";
import Typography from "../Typography";

export interface PortalProps {
  children: any;
}

export interface ModalProps {
  children: any;
  open: boolean;
  close: () => void;
  closeOnBackdropClick?: boolean;
  title?: string;
  width?: number;
  cta?: React.ReactNode;
}

const Portal: React.FC<PortalProps> = props => {
  const { children } = props;
  const modalRoot = document.body;
  const el = document.createElement("div");
  const focusedElBeforeOpen = document.activeElement;

  React.useEffect(() => {
    modalRoot.classList.add("modal-backdrop-bui");
    modalRoot.appendChild(el);
    return () => {
      modalRoot.classList.remove("modal-backdrop-bui");
      modalRoot.removeChild(el);
    };
  }, [modalRoot, el]);

  React.useEffect(() => {
    return () => (focusedElBeforeOpen as HTMLElement)?.focus();
  });

  return createPortal(children, el);
};

const Modal: React.FC<ModalProps> = props => {
  const {
    children,
    close,
    closeOnBackdropClick = true,
    cta,
    open = false,
    title,
    width
  } = props;
  const focusRefEl = React.useRef<HTMLDivElement>(null);

  const escFunction = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        close();
        document.removeEventListener("keydown", escFunction);
      }
    },
    [close]
  );

  React.useEffect(() => {
    if (open) {
      document.addEventListener("keydown", escFunction);
      focusRefEl.current.focus();
    }
  }, [open, escFunction]);

  return (
    open && (
      <Portal>
        <div
          className={ClassNames({
            [Styles.container]: true,
            [Styles.open]: open
          })}
          style={width ? { maxWidth: width } : undefined}
          role="dialog"
          aria-labelledby={title ? "bui-dialog-title" : undefined}
          ref={focusRefEl}
          tabIndex={0}
        >
          <div className={Styles.inner}>
            <ButtonIcon
              label="Close"
              icon={<IconClose is="svg" />}
              onClick={close}
              className={Styles.close}
            />
            {title && (
              <Typography
                variant="h3"
                className={Styles.title}
                id="bui-dialog-title"
              >
                {title}
              </Typography>
            )}
            <div id="bui-dialog-description">{children}</div>
            {cta && <div className="mt-6">{cta}</div>}
          </div>
        </div>
        <div
          className={ClassNames({
            [Styles.backdrop]: true,
            open
          })}
          onClick={closeOnBackdropClick ? close : undefined}
        />
      </Portal>
    )
  );
};

export default Modal;
